import React, { useEffect, useState } from 'react';
import { MatchSorterOptions, matchSorter } from 'match-sorter';
import useTranslation from 'next-translate/useTranslation';

// Types
import type { OptionType, SelectProps } from '@/components/base/Select';
import type { ReactElement } from 'react';

// Components
import Input from '@/components/base/Input';
import Icon from '@/components/base/Icon';
import Select from '@/components/base/Select';
import DefaultOption from '@/components/base/Select/DefaultOption';

type SelectFilterProps = SelectProps & {
    sortOptions?: MatchSorterOptions<OptionType>;
    idInput?: string;
    children?: ReactElement;
};

const SelectFilter = ({
    options = [],
    sortOptions = { keys: [{ threshold: matchSorter.rankings.CONTAINS, key: 'label' }] },
    idInput,
    children,
    ...restProps
}: SelectFilterProps) => {
    const { t } = useTranslation('commons');
    const [optionsData, setOptionsData] = useState(options ?? []);
    const [filter, setFilter] = useState('');

    useEffect(() => {
        if (filter !== '') {
            setOptionsData(matchSorter(options, filter, sortOptions));
        } else {
            setOptionsData(options);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, options]);

    return (
        <Select hasFilter={true} options={null} {...restProps}>
            <div className="sticky left-0 top-0 z-20 w-full bg-white pt-1 dark:bg-icoDarkMode-anthracite">
                <Input
                    beforeInput={<Icon icon="search" />}
                    inputStyle="search_select"
                    id={idInput}
                    className="mb-1"
                    noBorder={true}
                    value={filter}
                    onKeyDown={(event) => {
                        if (event.code === 'Space') {
                            event.stopPropagation();
                        }
                    }}
                    onChange={(e) => setFilter(e.target.value)}
                />
            </div>
            {!optionsData || (optionsData && !optionsData.length) ? (
                <div className="flex justify-center p-1">{t('select_no_options')}</div>
            ) : null}
            {optionsData?.map((option, id) => (
                <Select.Option
                    key={
                        typeof option?.value === 'string' || typeof option?.value === 'number'
                            ? option.value
                            : id
                    }
                    disabled={option?.disabled ?? null}
                    value={option}>
                    {({ selected, active, disabled }) => (
                        <>
                            {children ? (
                                React.Children.map(children, (child) => {
                                    const newChild = React.cloneElement(child, {
                                        selected,
                                        disabled,
                                        active,
                                        option
                                    });
                                    return newChild;
                                })
                            ) : (
                                <DefaultOption
                                    selected={selected}
                                    disabled={disabled}
                                    option={option}
                                />
                            )}
                        </>
                    )}
                </Select.Option>
            ))}
        </Select>
    );
};

export default SelectFilter;
